import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  X,
  RefreshCw,
  Settings,
  ExternalLink,
  Loader2,
  Clock,
  AlertCircle,
  AlertTriangle,
  Database,
  Bell,
  Calendar,
  BarChart3,
  Search,
  ShoppingBag,
  Megaphone,
  Youtube,
  Table2,
  HardDrive,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Platform, Connection, ConnectionStatus } from '../types';
import { TrendyolSettingsModal, TrendyolSettings } from './TrendyolSettingsModal';
import { HepsiburadaSettingsModal, HepsiburadaSettings } from './HepsiburadaSettingsModal';
import { CiceksepetiSettingsModal, CiceksepetiSettings } from './CiceksepetiSettingsModal';
import { PttAVMSettingsModal, PttAVMSettings } from './PttAVMSettingsModal';
import { N11SettingsModal, N11Settings } from './N11SettingsModal';
import { BeymenSettingsModal, BeymenSettings } from './BeymenSettingsModal';
import { SelectedProperties } from '../services/googleOAuthService';

// Platform logo paths mapping
const platformLogos: Record<string, string> = {
  // Marketplaces
  trendyol: '/logos/trendyol.png',
  hepsiburada: '/logos/hepsiburada.png',
  'amazon-tr': '/logos/amazon.webp',
  n11: '/logos/n11.png',
  ciceksepeti: '/logos/ciceksepeti.webp',
  pttavm: '/logos/pttavm.png',
  boyner: '/logos/boyner.png',
  beymen: '/logos/beymen.png',
  'turkcell-pasaj': '/logos/turkcell-pasaj.jpg',
  pazarama: '/logos/pazarama.png',
  // E-commerce Platforms
  shopify: '/logos/shopify.png',
  woocommerce: '/logos/woo.webp',
  ikas: '/logos/ikas.png',
  ideasoft: '/logos/ideasoft.jpg',
  ticimax: '/logos/ticimax.jpg',
  odoo: '/logos/odoo.webp',
  wix: '/logos/wix.png',
  tsoft: '/logos/t-soft.png',
  // Accounting & ERP
  parasut: '/logos/parasut.png',
  luca: '/logos/luca.jpg',
  mikro: '/logos/mikro.jpg',
  logo: '/logos/logo.png',
  // Analytics & Marketing
  hotjar: '/logos/hotjar.png',
  clarity: '/logos/clarity.png',
  'yandex-metrica': '/logos/yandex-metrica.jpg',
  criteo: '/logos/criteo.webp',
  'tiktok-ads': '/logos/tiktok.png',
  'meta-ads': '/logos/meta.png',
  'amazon-ads': '/logos/amazon-ads.jpg',
};

// Google services icons mapping
const googleServiceIcons = {
  'Google Analytics 4': BarChart3,
  'Google Search Console': Search,
  'Google Merchant Center': ShoppingBag,
  'Google Ads': Megaphone,
  'YouTube Analytics': Youtube,
  'Google Sheets': Table2,
  'Google Drive': HardDrive,
  'Gmail': Mail,
  'Google Calendar': Calendar,
};

interface ConnectionCardProps {
  platform: Platform;
  connection?: Connection;
  onConnect: (platformId: string) => void;
  onDisconnect: (connectionId: string) => void;
  onSync: (connectionId: string) => void;
  onSettings: (connectionId: string, settings: ConnectionSettings) => void;
  onConfigure?: (connectionId: string) => void;
  selectedProperties?: SelectedProperties | null;
}

// Map service display names to selectedProperties keys
const serviceToPropertyKey: Record<string, keyof SelectedProperties> = {
  'Google Analytics 4': 'analytics',
  'Google Search Console': 'searchConsole',
  'Google Merchant Center': 'merchant',
  'Google Ads': 'ads',
  'YouTube Analytics': 'youtube',
  'Gmail': 'gmail',
  'Google Calendar': 'calendar',
};

// All services are now configurable
const configurableServices = [
  'Google Analytics 4',
  'Google Search Console',
  'Google Merchant Center',
  'Google Ads',
  'YouTube Analytics',
  'Gmail',
  'Google Calendar'
];

interface ConnectionSettings {
  syncFrequency: number; // seconds
  dataTypes: string[];
  notifications: boolean;
}

const statusConfig: Record<ConnectionStatus, { color: string; icon: typeof Check; label: string }> = {
  connected: { color: 'text-green-500', icon: Check, label: 'Bağlı' },
  disconnected: { color: 'text-gray-400', icon: X, label: 'Bağlı Değil' },
  error: { color: 'text-red-500', icon: AlertCircle, label: 'Hata' },
  syncing: { color: 'text-blue-500', icon: RefreshCw, label: 'Senkronize Ediliyor' },
};

const syncFrequencyOptions = [
  { value: 3600, label: 'Saatlik', labelEn: 'Hourly' },
  { value: 21600, label: '6 Saatte Bir', labelEn: 'Every 6 Hours' },
  { value: 43200, label: '12 Saatte Bir', labelEn: 'Every 12 Hours' },
  { value: 86400, label: 'Günlük', labelEn: 'Daily' },
  { value: 604800, label: 'Haftalık', labelEn: 'Weekly' },
];

export function ConnectionCard({
  platform,
  connection,
  onConnect,
  onDisconnect,
  onSync,
  onSettings,
  onConfigure,
  selectedProperties,
}: ConnectionCardProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [isHovered, setIsHovered] = useState(false);

  // Modal states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState<ConnectionSettings>({
    syncFrequency: connection?.syncFrequency || 3600,
    dataTypes: ['sales', 'orders', 'products'],
    notifications: true,
  });

  const isConnected = connection?.status === 'connected';
  const isSyncing = connection?.status === 'syncing';
  const status = connection?.status || 'disconnected';
  const isGooglePlatform = platform.id === 'google';
  const isTrendyolPlatform = platform.id === 'trendyol';
  const isHepsiburadaPlatform = platform.id === 'hepsiburada';
  const isCiceksepetiPlatform = platform.id === 'ciceksepeti';
  const isPttavmPlatform = platform.id === 'pttavm';
  const isN11Platform = platform.id === 'n11';
  const isBeymenPlatform = platform.id === 'beymen';

  // Trendyol settings modal state
  const [showTrendyolSettings, setShowTrendyolSettings] = useState(false);

  // Hepsiburada settings modal state
  const [showHepsiburadaSettings, setShowHepsiburadaSettings] = useState(false);

  // Ciceksepeti settings modal state
  const [showCiceksepetiSettings, setShowCiceksepetiSettings] = useState(false);

  // PttAVM settings modal state
  const [showPttavmSettings, setShowPttavmSettings] = useState(false);

  // N11 settings modal state
  const [showN11Settings, setShowN11Settings] = useState(false);
  const [showBeymenSettings, setShowBeymenSettings] = useState(false);

  const handleTrendyolSettingsSave = (settings: TrendyolSettings) => {
    console.log('Trendyol settings saved:', settings);
    // TODO: Save to backend
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const handleHepsiburadaSettingsSave = (settings: HepsiburadaSettings) => {
    console.log('Hepsiburada settings saved:', settings);
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const handleCiceksepetiSettingsSave = (settings: CiceksepetiSettings) => {
    console.log('Ciceksepeti settings saved:', settings);
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const handlePttavmSettingsSave = (settings: PttAVMSettings) => {
    console.log('PttAVM settings saved:', settings);
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const handleN11SettingsSave = (settings: N11Settings) => {
    console.log('N11 settings saved:', settings);
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const handleBeymenSettingsSave = (settings: BeymenSettings) => {
    console.log('Beymen settings saved:', settings);
    onSettings(connection?.id || '', {
      syncFrequency: settings.syncFrequency,
      dataTypes: Object.entries(settings.integrations)
        .filter(([_, enabled]) => enabled)
        .map(([id]) => id),
      notifications: settings.notifications,
    });
  };

  const formatLastSync = (date?: Date) => {
    if (!date) return null;
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return isEnglish ? 'Just now' : 'Az önce';
    if (minutes < 60) return isEnglish ? `${minutes} min ago` : `${minutes} dakika önce`;
    if (hours < 24) return isEnglish ? `${hours} hours ago` : `${hours} saat önce`;
    return isEnglish ? `${days} days ago` : `${days} gün önce`;
  };

  const handleSaveSettings = () => {
    if (connection) {
      onSettings(connection.id, settingsForm);
    }
    setShowSettingsModal(false);
  };

  const handleConfirmDisconnect = () => {
    if (connection) {
      onDisconnect(connection.id);
    }
    setShowDisconnectModal(false);
  };

  const dataTypeOptions = [
    { id: 'sales', label: isEnglish ? 'Sales Data' : 'Satış Verileri', icon: Database },
    { id: 'orders', label: isEnglish ? 'Order Details' : 'Sipariş Detayları', icon: Database },
    { id: 'products', label: isEnglish ? 'Product Performance' : 'Ürün Performansı', icon: Database },
    { id: 'customers', label: isEnglish ? 'Customer Data' : 'Müşteri Verileri', icon: Database },
  ];

  // Special Google Platform Card
  if (isGooglePlatform) {
    return (
      <>
        <div
          className={cn(
            'relative rounded-2xl border-2 transition-all duration-300 overflow-hidden',
            isConnected
              ? 'bg-gradient-to-br from-blue-500/5 via-green-500/5 to-yellow-500/5 border-green-500/40 hover:border-green-500/60 shadow-lg shadow-green-500/10'
              : 'bg-gradient-to-br from-blue-500/10 via-red-500/5 to-yellow-500/10 border-blue-500/30 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20'
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Google Rainbow Top Border */}
          <div className="h-1 bg-gradient-to-r from-blue-500 via-red-500 via-yellow-500 to-green-500" />

          <div className="p-5 sm:p-6">
            {/* Header Row */}
            <div className="flex items-start gap-4 sm:gap-5">
              {/* Google Logo */}
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-10 h-10 sm:w-12 sm:h-12">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                {isConnected && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
                    <Check size={14} className="text-white" />
                  </div>
                )}
              </div>

              {/* Platform Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-text-primary">Google</h3>
                  {isConnected && (
                    <span className="px-2.5 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-xs font-semibold text-green-400">
                      {isEnglish ? 'Connected' : 'Bağlı'}
                    </span>
                  )}
                  <a
                    href={platform.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-tertiary hover:text-accent transition-colors"
                    title="API Docs"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
                <p className="text-sm sm:text-base text-text-secondary">
                  {isEnglish ? 'All Google services in one connection' : 'Tüm Google servisleri tek bağlantıda'}
                </p>
              </div>

              {/* Action Button */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isConnected ? (
                  <button
                    onClick={() => onConnect(platform.id)}
                    className="px-6 py-3 rounded-xl text-sm sm:text-base font-semibold bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 text-white hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 hover:scale-[1.02]"
                  >
                    {isEnglish ? 'Connect Google' : 'Google ile Bağlan'}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => connection && onSync(connection.id)}
                      disabled={isSyncing}
                      className="p-2.5 rounded-xl text-text-secondary hover:text-accent hover:bg-accent/10 transition-colors disabled:opacity-50"
                      title={isEnglish ? 'Sync Now' : 'Şimdi Senkronize Et'}
                    >
                      {isSyncing ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <RefreshCw size={20} />
                      )}
                    </button>
                    {onConfigure && (
                      <button
                        onClick={() => connection?.id && onConfigure(connection.id)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                        title={isEnglish ? 'Configure Services' : 'Servisleri Yapılandır'}
                      >
                        {isEnglish ? 'Configure' : 'Yapılandır'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowSettingsModal(true)}
                      className="p-2.5 rounded-xl text-text-secondary hover:text-accent hover:bg-accent/10 transition-colors"
                      title={isEnglish ? 'Settings' : 'Ayarlar'}
                    >
                      <Settings size={20} />
                    </button>
                    <button
                      onClick={() => setShowDisconnectModal(true)}
                      className="p-2.5 rounded-xl text-text-secondary hover:text-error hover:bg-error/10 transition-colors"
                      title={isEnglish ? 'Disconnect' : 'Bağlantıyı Kes'}
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Google Services Grid */}
            <div className="mt-5 pt-5 border-t border-border-default/50">
              <p className="text-xs text-text-tertiary uppercase tracking-wider font-medium mb-3">
                {isEnglish ? 'Included Services' : 'Dahil Servisler'}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
                {platform.features.map((service) => {
                  const Icon = googleServiceIcons[service as keyof typeof googleServiceIcons] || Database;
                  const propertyKey = serviceToPropertyKey[service];
                  const isConfigurable = configurableServices.includes(service);

                  // Check if service has selected properties
                  const selectedItems = selectedProperties?.[propertyKey];
                  const hasSelection = selectedItems && selectedItems.length > 0;

                  // Generate tooltip content
                  const getTooltipData = () => {
                    if (!isConnected) {
                      return { status: 'disconnected', title: service, subtitle: isEnglish ? 'Not connected' : 'Bağlı değil' };
                    }
                    if (!isConfigurable) {
                      return { status: 'coming', title: service, subtitle: isEnglish ? 'Coming soon' : 'Yakında' };
                    }
                    if (hasSelection) {
                      const items = selectedItems as Array<{
                        propertyName?: string;
                        siteUrl?: string;
                        descriptiveName?: string;
                        merchantId?: string;
                        title?: string;
                        name?: string;
                        summary?: string;
                        labelId?: string;
                        calendarId?: string;
                        spreadsheetId?: string;
                        fileId?: string;
                      }>;
                      const names = items.map(item =>
                        item.propertyName || item.name || item.summary || item.siteUrl || item.descriptiveName || item.merchantId || item.title || item.labelId || item.calendarId
                      ).filter(Boolean);
                      return { status: 'active', title: service, subtitle: isEnglish ? 'Active' : 'Aktif', items: names };
                    }
                    return { status: 'inactive', title: service, subtitle: isEnglish ? 'Not configured' : 'Yapılandırılmadı' };
                  };

                  const tooltipData = getTooltipData();

                  // Determine status color
                  const getStatusStyle = () => {
                    if (!isConnected) {
                      return 'bg-bg-elevated/50 border border-border-default hover:border-accent/30 hover:bg-accent/5';
                    }
                    if (!isConfigurable) {
                      return 'bg-gray-500/10 border border-gray-500/20 hover:bg-gray-500/15';
                    }
                    if (hasSelection) {
                      return 'bg-green-500/10 border border-green-500/20 hover:bg-green-500/15';
                    }
                    return 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/15';
                  };

                  const getIconColor = () => {
                    if (!isConnected) return 'text-text-secondary';
                    if (!isConfigurable) return 'text-gray-400';
                    if (hasSelection) return 'text-green-400';
                    return 'text-red-400';
                  };

                  return (
                    <div
                      key={service}
                      className={cn(
                        'flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-all group relative',
                        getStatusStyle()
                      )}
                    >
                      <Icon
                        size={20}
                        className={getIconColor()}
                      />
                      <span className="text-[10px] text-text-tertiary text-center leading-tight truncate w-full">
                        {service.replace('Google ', '').replace(' 4', '')}
                      </span>
                      {/* Status indicator dot */}
                      {isConnected && isConfigurable && (
                        <div className={cn(
                          'absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-bg-card',
                          hasSelection ? 'bg-green-500' : 'bg-red-500'
                        )} />
                      )}
                      {/* Modern Custom Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                        <div className={cn(
                          'px-3 py-2 rounded-lg shadow-xl backdrop-blur-sm min-w-[140px] max-w-[200px]',
                          'bg-gray-900/95 border border-white/10'
                        )}>
                          {/* Status Badge */}
                          <div className="flex items-center gap-2 mb-1">
                            <div className={cn(
                              'w-2 h-2 rounded-full',
                              tooltipData.status === 'active' && 'bg-green-400',
                              tooltipData.status === 'inactive' && 'bg-red-400',
                              tooltipData.status === 'coming' && 'bg-gray-400',
                              tooltipData.status === 'disconnected' && 'bg-gray-500'
                            )} />
                            <span className={cn(
                              'text-xs font-medium',
                              tooltipData.status === 'active' && 'text-green-400',
                              tooltipData.status === 'inactive' && 'text-red-400',
                              tooltipData.status === 'coming' && 'text-gray-400',
                              tooltipData.status === 'disconnected' && 'text-gray-500'
                            )}>
                              {tooltipData.subtitle}
                            </span>
                          </div>
                          {/* Service Name */}
                          <p className="text-xs text-white/90 font-medium">{tooltipData.title}</p>
                          {/* Selected Items */}
                          {tooltipData.items && tooltipData.items.length > 0 && (
                            <div className="mt-1.5 pt-1.5 border-t border-white/10">
                              {tooltipData.items.slice(0, 3).map((item, idx) => (
                                <p key={idx} className="text-[10px] text-white/60 truncate">• {item}</p>
                              ))}
                              {tooltipData.items.length > 3 && (
                                <p className="text-[10px] text-white/40">+{tooltipData.items.length - 3} {isEnglish ? 'more' : 'daha'}</p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900/95" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Connection Benefit */}
            <div className="mt-4 p-4 rounded-xl bg-bg-elevated/30 border border-border-default/50">
              <p className="text-sm text-text-tertiary leading-relaxed">
                {platform.connectionBenefit}
              </p>
            </div>

            {/* Last Sync Info */}
            {isConnected && connection?.lastSync && (
              <div className="flex items-center gap-1.5 text-xs text-text-tertiary mt-4">
                <Clock size={12} />
                <span>{isEnglish ? 'Last sync:' : 'Son senkronizasyon:'} {formatLastSync(connection.lastSync)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Settings Modal - Reuse the same modals */}
        {showSettingsModal && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowSettingsModal(false)}
            />
            <div className="relative bg-bg-card border border-border-default rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-border-default">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white shadow flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary">Google</h3>
                    <p className="text-xs text-text-secondary">{isEnglish ? 'Connection Settings' : 'Bağlantı Ayarları'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-5">
                {/* Sync Frequency */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                    <Calendar size={16} className="text-accent" />
                    {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sıklığı'}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {syncFrequencyOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSettingsForm({ ...settingsForm, syncFrequency: option.value })}
                        className={cn(
                          'px-3 py-2.5 rounded-lg text-sm font-medium transition-all border',
                          settingsForm.syncFrequency === option.value
                            ? 'border-accent bg-accent/10 text-accent'
                            : 'border-border-default text-text-secondary hover:border-accent/50'
                        )}
                      >
                        {isEnglish ? option.labelEn : option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notifications */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                    <Bell size={16} className="text-accent" />
                    {isEnglish ? 'Notifications' : 'Bildirimler'}
                  </label>
                  <label className="flex items-center justify-between p-3 rounded-lg border border-border-default cursor-pointer hover:border-accent/50 transition-all">
                    <span className="text-sm text-text-secondary">
                      {isEnglish ? 'Notify me when sync completes' : 'Senkronizasyon tamamlandığında bildir'}
                    </span>
                    <div
                      onClick={() => setSettingsForm({ ...settingsForm, notifications: !settingsForm.notifications })}
                      className={cn(
                        'w-11 h-6 rounded-full relative cursor-pointer transition-colors',
                        settingsForm.notifications ? 'bg-accent' : 'bg-bg-elevated'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                          settingsForm.notifications ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-5 border-t border-border-default">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                >
                  {isEnglish ? 'Cancel' : 'İptal'}
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-colors"
                >
                  {isEnglish ? 'Save Settings' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Disconnect Modal */}
        {showDisconnectModal && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowDisconnectModal(false)}
            />
            <div className="relative bg-bg-card border border-border-default rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="pt-6 pb-4 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center">
                  <AlertTriangle size={32} className="text-error" />
                </div>
              </div>
              <div className="px-6 pb-4 text-center">
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {isEnglish ? 'Disconnect Google?' : 'Google Bağlantısını Kes?'}
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {isEnglish
                    ? 'Are you sure? This will disconnect all Google services including Analytics, Search Console, Ads, and more.'
                    : 'Emin misiniz? Bu işlem Analytics, Search Console, Ads ve diğer tüm Google servislerinin bağlantısını kesecek.'}
                </p>
              </div>
              <div className="flex items-center gap-3 p-5 border-t border-border-default">
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
                >
                  {isEnglish ? 'Cancel' : 'İptal'}
                </button>
                <button
                  onClick={handleConfirmDisconnect}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-error text-white hover:bg-error/90 transition-colors"
                >
                  {isEnglish ? 'Disconnect' : 'Bağlantıyı Kes'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // Regular Platform Card
  return (
    <>
      <div
        className={cn(
          'relative rounded-xl border transition-all duration-200 overflow-hidden',
          isConnected
            ? 'bg-bg-card border-green-500/30 hover:border-green-500/50'
            : 'bg-bg-card border-border-default hover:border-border-hover',
          platform.comingSoon && 'opacity-60'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="p-4 sm:p-5">
          {/* Main Row - Platform info and action */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Platform Icon/Logo - App Icon Style */}
            <div
              className={cn(
                "w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden",
                platformLogos[platform.id]
                  ? "bg-white shadow-lg shadow-black/10 ring-1 ring-black/5"
                  : ""
              )}
              style={{
                backgroundColor: platformLogos[platform.id] ? 'white' : platform.color + '20',
                boxShadow: platformLogos[platform.id]
                  ? '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)'
                  : undefined
              }}
            >
              {platformLogos[platform.id] ? (
                <img
                  src={platformLogos[platform.id]}
                  alt={platform.name}
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                <span style={{ color: platform.color }} className="text-lg sm:text-xl font-bold">{platform.name.charAt(0)}</span>
              )}
            </div>

            {/* Platform Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-semibold text-text-primary">{platform.name}</h3>
                {platform.comingSoon && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-[10px] sm:text-xs font-medium text-amber-400 whitespace-nowrap">
                    {isEnglish ? 'Coming Soon' : 'Yakında'}
                  </span>
                )}
                {isConnected && (
                  <span className="flex items-center gap-1 text-green-500 text-xs sm:text-sm">
                    <Check size={14} />
                    {isEnglish ? 'Connected' : 'Bağlı'}
                  </span>
                )}
                {platform.docsUrl && !platform.comingSoon && (
                  <a
                    href={platform.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-tertiary hover:text-accent transition-colors flex-shrink-0"
                    title="API Docs"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
              <p className="text-sm text-text-secondary mt-0.5">{platform.description}</p>
            </div>

            {/* Action Button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {platform.comingSoon ? (
                <button
                  disabled
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-border-default text-text-tertiary cursor-not-allowed"
                >
                  {isEnglish ? 'Soon' : 'Yakında'}
                </button>
              ) : !isConnected ? (
                <button
                  onClick={() => onConnect(platform.id)}
                  className="px-4 sm:px-5 py-2 rounded-lg text-sm font-medium border-2 border-accent text-accent hover:bg-accent/10 transition-colors"
                >
                  {isEnglish ? 'Connect' : 'Bağlan'}
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => connection && onSync(connection.id)}
                    disabled={isSyncing}
                    className="p-2 rounded-lg text-text-secondary hover:text-accent hover:bg-accent/10 transition-colors disabled:opacity-50"
                    title={isEnglish ? 'Sync Now' : 'Şimdi Senkronize Et'}
                  >
                    {isSyncing ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <RefreshCw size={18} />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (isTrendyolPlatform) {
                        setShowTrendyolSettings(true);
                      } else if (isHepsiburadaPlatform) {
                        setShowHepsiburadaSettings(true);
                      } else if (isCiceksepetiPlatform) {
                        setShowCiceksepetiSettings(true);
                      } else if (isPttavmPlatform) {
                        setShowPttavmSettings(true);
                      } else if (isN11Platform) {
                        setShowN11Settings(true);
                      } else if (isBeymenPlatform) {
                        setShowBeymenSettings(true);
                      } else {
                        setShowSettingsModal(true);
                      }
                    }}
                    className={cn(
                      "p-2 rounded-lg text-text-secondary transition-colors",
                      isTrendyolPlatform
                        ? "hover:text-[#F27A1A] hover:bg-[#F27A1A]/10"
                        : isHepsiburadaPlatform
                        ? "hover:text-[#FF6000] hover:bg-[#FF6000]/10"
                        : isCiceksepetiPlatform
                        ? "hover:text-[#7B2D8E] hover:bg-[#7B2D8E]/10"
                        : isPttavmPlatform
                        ? "hover:text-[#FFD700] hover:bg-[#FFD700]/10"
                        : isN11Platform
                        ? "hover:text-[#00B14F] hover:bg-[#00B14F]/10"
                        : "hover:text-accent hover:bg-accent/10"
                    )}
                    title={isEnglish ? 'Settings' : 'Ayarlar'}
                  >
                    <Settings size={18} />
                  </button>
                  <button
                    onClick={() => setShowDisconnectModal(true)}
                    className="p-2 rounded-lg text-text-secondary hover:text-error hover:bg-error/10 transition-colors"
                    title={isEnglish ? 'Disconnect' : 'Bağlantıyı Kes'}
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Connection Benefit Description */}
          <div className="mt-3 pt-3 border-t border-border-default">
            <p className="text-sm text-text-tertiary leading-relaxed">
              {platform.connectionBenefit}
            </p>
          </div>

          {/* Last Sync Info */}
          {isConnected && connection?.lastSync && (
            <div className="flex items-center gap-1.5 text-xs text-text-tertiary mt-3">
              <Clock size={12} />
              <span>{isEnglish ? 'Last sync:' : 'Son senkronizasyon:'} {formatLastSync(connection.lastSync)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowSettingsModal(false)}
          />
          <div className="relative bg-bg-card border border-border-default rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border-default">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden",
                    platformLogos[platform.id] ? "bg-white ring-1 ring-black/5" : ""
                  )}
                  style={{
                    backgroundColor: platformLogos[platform.id] ? 'white' : platform.color + '20',
                    boxShadow: platformLogos[platform.id]
                      ? '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.5)'
                      : undefined
                  }}
                >
                  {platformLogos[platform.id] ? (
                    <img
                      src={platformLogos[platform.id]}
                      alt={platform.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <span style={{ color: platform.color }} className="font-bold">{platform.name.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{platform.name}</h3>
                  <p className="text-xs text-text-secondary">{isEnglish ? 'Connection Settings' : 'Bağlantı Ayarları'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-elevated transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-5">
              {/* Sync Frequency */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Calendar size={16} className="text-accent" />
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sıklığı'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {syncFrequencyOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSettingsForm({ ...settingsForm, syncFrequency: option.value })}
                      className={cn(
                        'px-3 py-2.5 rounded-lg text-sm font-medium transition-all border',
                        settingsForm.syncFrequency === option.value
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border-default text-text-secondary hover:border-accent/50'
                      )}
                    >
                      {isEnglish ? option.labelEn : option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data Types */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Database size={16} className="text-accent" />
                  {isEnglish ? 'Data to Sync' : 'Senkronize Edilecek Veriler'}
                </label>
                <div className="space-y-2">
                  {dataTypeOptions.map((option) => (
                    <label
                      key={option.id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                        settingsForm.dataTypes.includes(option.id)
                          ? 'border-accent bg-accent/5'
                          : 'border-border-default hover:border-accent/50'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={settingsForm.dataTypes.includes(option.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSettingsForm({ ...settingsForm, dataTypes: [...settingsForm.dataTypes, option.id] });
                          } else {
                            setSettingsForm({ ...settingsForm, dataTypes: settingsForm.dataTypes.filter(t => t !== option.id) });
                          }
                        }}
                        className="w-4 h-4 rounded border-border-default text-accent focus:ring-accent"
                      />
                      <span className="text-sm text-text-primary">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notifications */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-3">
                  <Bell size={16} className="text-accent" />
                  {isEnglish ? 'Notifications' : 'Bildirimler'}
                </label>
                <label className="flex items-center justify-between p-3 rounded-lg border border-border-default cursor-pointer hover:border-accent/50 transition-all">
                  <span className="text-sm text-text-secondary">
                    {isEnglish ? 'Notify me when sync completes' : 'Senkronizasyon tamamlandığında bildir'}
                  </span>
                  <div
                    onClick={() => setSettingsForm({ ...settingsForm, notifications: !settingsForm.notifications })}
                    className={cn(
                      'w-11 h-6 rounded-full relative cursor-pointer transition-colors',
                      settingsForm.notifications ? 'bg-accent' : 'bg-bg-elevated'
                    )}
                  >
                    <div
                      className={cn(
                        'w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform',
                        settingsForm.notifications ? 'translate-x-5' : 'translate-x-0.5'
                      )}
                    />
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-5 border-t border-border-default">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
              >
                {isEnglish ? 'Cancel' : 'İptal'}
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-colors"
              >
                {isEnglish ? 'Save Settings' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDisconnectModal(false)}
          />
          <div className="relative bg-bg-card border border-border-default rounded-2xl shadow-2xl w-full max-w-sm">
            {/* Icon */}
            <div className="pt-6 pb-4 flex justify-center">
              <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center">
                <AlertTriangle size={32} className="text-error" />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-4 text-center">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {isEnglish ? 'Disconnect' : 'Bağlantıyı Kes'} {platform.name}?
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {isEnglish
                  ? `Are you sure you want to disconnect ${platform.name}? All synced data will be removed and you'll need to reconnect to sync again.`
                  : `${platform.name} bağlantısını kesmek istediğinizden emin misiniz? Tüm senkronize edilmiş veriler silinecek ve tekrar bağlanmanız gerekecek.`
                }
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 p-5 border-t border-border-default">
              <button
                onClick={() => setShowDisconnectModal(false)}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-border-default text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
              >
                {isEnglish ? 'Cancel' : 'İptal'}
              </button>
              <button
                onClick={handleConfirmDisconnect}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-error text-white hover:bg-error/90 transition-colors"
              >
                {isEnglish ? 'Disconnect' : 'Bağlantıyı Kes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Trendyol Settings Modal */}
      {isTrendyolPlatform && (
        <TrendyolSettingsModal
          isOpen={showTrendyolSettings}
          onClose={() => setShowTrendyolSettings(false)}
          sellerId={(connection?.settings?.sellerId as string) || ''}
          onSave={handleTrendyolSettingsSave}
        />
      )}

      {/* Hepsiburada Settings Modal */}
      {isHepsiburadaPlatform && (
        <HepsiburadaSettingsModal
          isOpen={showHepsiburadaSettings}
          onClose={() => setShowHepsiburadaSettings(false)}
          merchantId={(connection?.merchantId as string) || (connection?.settings?.merchantId as string) || ''}
          connectionId={connection?.id || ''}
          onSave={handleHepsiburadaSettingsSave}
        />
      )}

      {/* Ciceksepeti Settings Modal */}
      {isCiceksepetiPlatform && (
        <CiceksepetiSettingsModal
          isOpen={showCiceksepetiSettings}
          onClose={() => setShowCiceksepetiSettings(false)}
          connectionId={connection?.id || ''}
          onSave={handleCiceksepetiSettingsSave}
        />
      )}

      {/* PttAVM Settings Modal */}
      {isPttavmPlatform && (
        <PttAVMSettingsModal
          isOpen={showPttavmSettings}
          onClose={() => setShowPttavmSettings(false)}
          connectionId={connection?.id || ''}
          onSave={handlePttavmSettingsSave}
        />
      )}

      {/* N11 Settings Modal */}
      {isN11Platform && (
        <N11SettingsModal
          isOpen={showN11Settings}
          onClose={() => setShowN11Settings(false)}
          storeName={(connection?.metadata?.storeName as string) || 'N11 Magaza'}
          connectionId={connection?.id || ''}
          onSave={handleN11SettingsSave}
        />
      )}

      {/* Beymen Settings Modal */}
      {isBeymenPlatform && (
        <BeymenSettingsModal
          isOpen={showBeymenSettings}
          onClose={() => setShowBeymenSettings(false)}
          storeName={(connection?.metadata?.storeName as string) || 'Beymen Magaza'}
          connectionId={connection?.id || ''}
          onSave={handleBeymenSettingsSave}
        />
      )}
    </>
  );
}
